import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useEffect,
  useState,
} from "react";
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  MarkerType,
  useReactFlow,
} from "reactflow";
import "reactflow/dist/style.css";
import StageNode from "./StageNode";
import ConfirmDialog from "../common/ConfirmDialog";
import { createClientId } from "../../utils/id";

const nodeTypes = { stage: StageNode };

const WorkflowCanvas = forwardRef(function WorkflowCanvas(
  {
    nodes,
    edges,
    setNodes,
    setEdges,
    onNodeClick,
    onNodeDelete,
    onEdgeLabelEdit,
    onSelectionChange,
    onNodeDragStart,
    onNodeDragStop,
  },
  ref,
) {
  const rf = useRef(null);
  const { project, fitView } = useReactFlow();
  const [minimap, setMinimap] = useState(true);
  const [deleteNodeConfirm, setDeleteNodeConfirm] = useState(null);
  const [edgeMenu, setEdgeMenu] = useState({
    edge: null,
    x: 0,
    y: 0,
    open: false,
  });

  useEffect(() => {
    setEdges((current) => {
      let changed = false;
      const next = current.map((edge) => {
        const nextMarkerEnd = {
          type: MarkerType.Arrow,
          height: 18,
          width: 18,
          ...(edge.markerEnd || {}),
        };
        const needsUpdate =
          !edge.markerEnd ||
          edge.markerEnd.height !== 18 ||
          edge.markerEnd.width !== 18 ||
          !edge.markerStart ||
          edge.markerStart.height !== 18 ||
          edge.markerStart.width !== 18;
        if (!needsUpdate) return edge;
        changed = true;
        return {
          ...edge,
          markerEnd: nextMarkerEnd,
        };
      });
      return changed ? next : current;
    });
  }, [setEdges]);

  const onConnect = useCallback(
    (params) => {
      const id = createClientId("edge");
      setEdges((es) =>
        addEdge(
          {
            ...params,
            id,
            animated: false,
            style: { stroke: "#888" },
            markerEnd: { type: MarkerType.Arrow, height: 25, width: 25 },
          },
          es,
        ),
      );
    },
    [setEdges],
  );

  const onNodesChange = useCallback(
    (changes) => setNodes((nds) => applyNodeChanges(changes, nds)),
    [setNodes],
  );

  const onEdgesChange = useCallback(
    (changes) => setEdges((eds) => applyEdgeChanges(changes, eds)),
    [setEdges],
  );

  const handleNodeContextMenu = useCallback(
    (evt, node) => {
      evt.preventDefault();
      const nodeName = node?.data?.name || node?.data?.label || "this stage";
      setDeleteNodeConfirm({ nodeId: node.id, nodeName });
    },
    [],
  );

  const handleEdgeDoubleClick = useCallback(
    (evt, edge) => {
      evt.preventDefault();
      const newLabel = window.prompt("Edge label", edge.label || "");
      if (newLabel !== null) {
        onEdgeLabelEdit(edge.id, newLabel);
      }
    },
    [onEdgeLabelEdit],
  );

  const handleEdgeEdit = useCallback(
    (edge) => {
      if (!edge) return;
      const newLabel = window.prompt("Edge label", edge.label || "");
      if (newLabel !== null) {
        onEdgeLabelEdit(edge.id, newLabel);
      }
    },
    [onEdgeLabelEdit],
  );

  const handleEdgeDelete = useCallback(
    (edge) => {
      if (!edge) return;
      setEdges((es) => es.filter((e) => e.id !== edge.id));
      setEdgeMenu((prev) => ({ ...prev, open: false, edge: null }));
    },
    [setEdges],
  );

  const handleEdgeContextMenu = useCallback((evt, edge) => {
    evt.preventDefault();
    setEdgeMenu({
      edge,
      x: evt.clientX,
      y: evt.clientY,
      open: true,
    });
  }, []);

  const handleNodeClick = useCallback(
    (evt, node) => {
      onNodeClick(evt, node);
    },
    [onNodeClick],
  );

  const handleSelectionChange = useCallback(
    (selection) => {
      onSelectionChange(selection);
    },
    [onSelectionChange],
  );

  const handleEdgeClick = useCallback((evt, edge) => {
    evt.preventDefault();
    setEdgeMenu({
      edge,
      x: evt.clientX,
      y: evt.clientY,
      open: true,
    });
  }, []);

  const closeEdgeMenu = useCallback(() => {
    setEdgeMenu((prev) =>
      prev.open ? { ...prev, open: false, edge: null } : prev,
    );
  }, []);

  const nodesWithBranchInfo = useMemo(() => {
    const outgoingCounts = {};
    edges.forEach((edge) => {
      outgoingCounts[edge.source] = (outgoingCounts[edge.source] || 0) + 1;
    });
    return nodes.map((node) => ({
      ...node,
      data: {
        ...node.data,
        outgoingCount: outgoingCounts[node.id] || 0,
      },
    }));
  }, [nodes, edges]);

  useImperativeHandle(ref, () => ({
    addNodeAtCenter(newNode) {
      try {
        const viewport = project?.getViewport
          ? project.getViewport()
          : { x: 0, y: 0, zoom: 1 };
        const offset = nodes.length * 26;
        const center = {
          x: viewport.x + window.innerWidth / 2 / viewport.zoom - 150 + offset,
          y: viewport.y + window.innerHeight / 2 / viewport.zoom - 40 + offset,
        };
        const node = { ...newNode, position: center, type: "stage" };
        setNodes((ns) => ns.concat(node));
      } catch (err) {
        setNodes((ns) =>
          ns.concat({ ...newNode, position: { x: 0, y: 0 }, type: "stage" }),
        );
      }
    },
  }));

  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }}>
      <ReactFlow
        nodes={nodesWithBranchInfo}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        nodeTypes={nodeTypes}
        onNodeClick={handleNodeClick}
        onNodeDragStart={onNodeDragStart}
        onNodeDragStop={onNodeDragStop}
        onNodeContextMenu={handleNodeContextMenu}
        onEdgeDoubleClick={handleEdgeDoubleClick}
        onEdgeContextMenu={handleEdgeContextMenu}
        onSelectionChange={handleSelectionChange}
        onEdgeClick={handleEdgeClick}
        onPaneClick={closeEdgeMenu}
        fitView
        style={{
          background:
            "radial-gradient(circle at 20% 20%, rgba(99,102,241,0.08), transparent 40%), radial-gradient(circle at 80% 0%, rgba(14,165,233,0.08), transparent 35%), #ffffff",
        }}
        ref={rf}
      >
        <Background gap={16} size={1} />
        <Controls />
        {minimap && <MiniMap />}
      </ReactFlow>

      {nodes.length === 0 && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              background: "#ffffff",
              border: "1px solid #e5e7eb",
              borderRadius: 12,
              padding: "16px 18px",
              boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
              textAlign: "center",
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 16, color: "#111827" }}>
              Build your workflow
            </div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
              Click + Add to place your first stage.
            </div>
          </div>
        </div>
      )}

      {edgeMenu.open && edgeMenu.edge && (
        <div
          style={{
            position: "fixed",
            left: edgeMenu.x + 8,
            top: edgeMenu.y + 8,
            zIndex: 2000,
            background: "#ffffff",
            border: "1px solid #e5e7eb",
            borderRadius: 10,
            boxShadow: "0 12px 30px rgba(15,23,42,0.18)",
            display: "flex",
            gap: 6,
            padding: 6,
          }}
        >
          <button
            onClick={() => handleEdgeEdit(edgeMenu.edge)}
            style={{
              padding: "6px 10px",
              borderRadius: 8,
              border: "1px solid #e5e7eb",
              background: "#fff",
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            Label
          </button>
          <button
            onClick={() => handleEdgeDelete(edgeMenu.edge)}
            style={{
              padding: "6px 10px",
              borderRadius: 8,
              border: "1px solid #fecaca",
              background: "#fee2e2",
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 600,
              color: "#991b1b",
            }}
          >
            Delete
          </button>
        </div>
      )}

      <div
        style={{
          position: "absolute",
          right: 12,
          top: 12,
          display: "flex",
          gap: 8,
          background: "#ffffff",
          border: "1px solid #e5e7eb",
          borderRadius: 10,
          padding: 6,
          boxShadow: "0 10px 25px rgba(15,23,42,0.08)",
        }}
      >
        <button
          onClick={() => fitView?.({ padding: 0.2 })}
          style={{
            padding: "6px 8px",
            borderRadius: 8,
            border: "1px solid #e5e7eb",
            background: "#fff",
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          Fit View
        </button>
        <button
          onClick={() => setMinimap((v) => !v)}
          style={{
            padding: "6px 8px",
            borderRadius: 8,
            border: "1px solid #e5e7eb",
            background: minimap ? "#eef2ff" : "#fff",
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          Mini-map
        </button>
      </div>
      {deleteNodeConfirm && (
        <ConfirmDialog
          title="Delete Stage"
          message={`Delete "${deleteNodeConfirm.nodeName}"? This will also remove its connections.`}
          confirmLabel="Delete"
          confirmClass="btn-danger"
          onConfirm={() => { const id = deleteNodeConfirm.nodeId; setDeleteNodeConfirm(null); onNodeDelete(id); }}
          onClose={() => setDeleteNodeConfirm(null)}
        />
      )}
    </div>
  );
});

export default WorkflowCanvas;
